import React, { useState, useEffect, useRef, useCallback } from "react";
import {
    Layout, Table, Input, Button, Form, Space, Row, Col,
    Breadcrumb, Modal, message, Typography, Tooltip, Switch, Select, Tag, Popconfirm,
    Descriptions
} from "antd";
import Headerdiv from "../layout/header";
import Footerdiv from "../layout/footer";
import {
    PlusOutlined, EditOutlined, HomeOutlined, ShoppingCartOutlined,
    SearchOutlined, EyeOutlined,
    FilterOutlined, ClearOutlined, DeleteOutlined,
    FilePdfOutlined, FileExcelOutlined, ScanOutlined
} from "@ant-design/icons";
import api from "../../services/axiosInstance";
import BarcodeScannerModal from "../../components/common/BarcodeScannerModal";
import Highlighter from "react-highlight-words";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import dayjs from "dayjs";
import { isPermissionAllowed } from "../../utils/permissionHelper";
import "../../styles/fluidDesign.css";

const { Content } = Layout;
const { Text } = Typography;
const { Option } = Select;

// Hardcoded product types for now (no type master table yet).
const PRODUCT_TYPE_OPTIONS = [
    { value: 1, label: "Single" },
    { value: 2, label: "Linked Product" },
];

const productTypeLabel = (value) =>
    PRODUCT_TYPE_OPTIONS.find((opt) => opt.value === Number(value))?.label || "—";

const fmtMoney = (value) =>
    `LKR ${Number(value || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Upper bound for a single export request (kept in sync with the API's limit cap).
const EXPORT_LIMIT = 1000;

const ManageSalesProducts = () => {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [uoms, setUoms] = useState([]);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [currentItem, setCurrentItem] = useState(null);
    const [viewItem, setViewItem] = useState(null);
    const [isViewVisible, setIsViewVisible] = useState(false);
    const [addForm] = Form.useForm();
    const [editForm] = Form.useForm();

    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const [isAddModalVisible, setIsAddModalVisible] = useState(false);

    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef(null);

    // Barcode scanner modal ('add' | 'edit' decides which form gets the code)
    const [scannerOpen, setScannerOpen] = useState(false);
    const [scanTarget, setScanTarget] = useState('add');

    // Top filter bar state (server-side search + category + status)
    const [search, setSearch] = useState('');
    const [filterCat, setFilterCat] = useState(undefined);
    const [filterStatus, setFilterStatus] = useState('all');
    const [applied, setApplied] = useState({});

    const canManage = isPermissionAllowed('sales_products_manage');

    /* ─── column search helpers ─────────────────────────────── */
    const handleSearch = (selectedKeys, confirm, dataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };
    const handleReset = (clearFilters) => { clearFilters(); setSearchText(''); };

    const getColumnSearchProps = (dataIndex) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button type="primary" onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                        icon={<SearchOutlined />} size="small" style={{ width: 90 }}>Search</Button>
                    <Button onClick={() => clearFilters && handleReset(clearFilters)} size="small" style={{ width: 90 }}>Reset</Button>
                    <Button type="link" size="small" onClick={() => { confirm({ closeDropdown: false }); setSearchText(selectedKeys[0]); setSearchedColumn(dataIndex); }}>Filter</Button>
                    <Button type="link" size="small" onClick={() => close()}>close</Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />,
        onFilter: (value, record) => record[dataIndex]?.toString().toLowerCase().includes(value.toLowerCase()),
        filterDropdownProps: { onOpenChange(open) { if (open) setTimeout(() => searchInput.current?.select(), 100); } },
        render: (text) => searchedColumn === dataIndex ? (
            <Highlighter
                highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                searchWords={[searchText]} autoEscape
                textToHighlight={text ? text.toString() : ''}
            />
        ) : text,
    });

    /* ─── dropdown data ──────────────────────────────────────── */
    const fetchCategories = useCallback(async () => {
        try {
            const res = await api.get('/masterdropdown/listof_salesproductcategories.php');
            setCategories(res.data.dataset || []);
        } catch {
            message.error("Failed to load product categories.");
        }
    }, []);

    const fetchUoms = useCallback(async () => {
        try {
            const res = await api.get('/masterdropdown/listof_activeuoms.php');
            setUoms(res.data.dataset || []);
        } catch {
            message.error("Failed to load units of measurement.");
        }
    }, []);

    /* ─── fetch ──────────────────────────────────────────────── */
    const mapRow = (item) => ({
        spId: item.sp_id,
        spCode: item.sp_code,
        spName: item.sp_name,
        spBarcode: item.sp_barcode || '',
        spcId: item.spc_id,
        categoryName: item.category_name || '',
        uomId: item.uom_id || null,
        uomName: item.uom_name || '',
        uomSymbol: item.uom_symbol || '',
        spType: item.sp_type,
        spUnitKg: item.sp_unit_kg,
        spOpeningquantity: item.sp_openingquantity,
        spCostprice: item.sp_costprice,
        spSellingprice: item.sp_sellingprice,
        spSpecialprice: item.sp_specialprice,
        isActive: item.is_active,
        createdAt: item.created_at,
        updatedAt: item.updated_at,
    });

    const fetchProducts = useCallback(async (page = 1, pageSize = 10, filters = {}) => {
        try {
            setLoading(true);
            const res = await api.get(`/salesproducts/list.php`, {
                params: {
                    page,
                    limit: pageSize,
                    search: filters.search || '',
                    spc_id: filters.spc_id || 0,
                    is_active: filters.is_active ?? -1,
                },
            });
            setProducts((res.data.dataset || []).map(mapRow));
            setPagination({
                current: res.data.pagination?.current_page || 1,
                pageSize: res.data.pagination?.per_page || 10,
                total: res.data.pagination?.total_records || 0,
            });
        } catch {
            message.error("Failed to fetch sales products.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchProducts(1, 10, {});
        fetchCategories();
        fetchUoms();
    }, [fetchProducts, fetchCategories, fetchUoms]);

    const handleTableChange = (pg) => fetchProducts(pg.current, pg.pageSize, applied);

    const handleFilter = () => {
        const filters = {
            search: search.trim(),
            spc_id: filterCat || 0,
            is_active: filterStatus === 'active' ? 1 : filterStatus === 'inactive' ? 0 : -1,
        };
        setApplied(filters);
        fetchProducts(1, pagination.pageSize, filters);
    };

    const handleClear = () => {
        setSearch('');
        setFilterCat(undefined);
        setFilterStatus('all');
        setApplied({});
        fetchProducts(1, pagination.pageSize, {});
    };

    /* ─── add ────────────────────────────────────────────────── */
    const handleAdd = async (values) => {
        try {
            setLoading(true);
            const res = await api.post('/salesproducts/add.php', {
                sp_code: values.spCode,
                sp_name: values.spName,
                spc_id: values.spcId,
                uom_id: values.uomId,
                sp_type: values.spType,
                sp_barcode: values.spBarcode || '',
                sp_unit_kg: values.spUnitKg,
                sp_openingquantity: values.spOpeningquantity,
                sp_costprice: values.spCostprice,
                sp_sellingprice: values.spSellingprice,
                sp_specialprice: values.spSpecialprice,
            });
            if (res.data.status === 'success') {
                message.success(res.data.message || "Sales product added successfully.");
                setIsAddModalVisible(false);
                fetchProducts(1, pagination.pageSize, applied);
                addForm.resetFields();
            } else throw new Error(res.data.message);
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to add sales product.");
        } finally { setLoading(false); }
    };

    /* ─── view full details ──────────────────────────────────── */
    const openView = (record) => {
        setViewItem(record);
        setIsViewVisible(true);
    };

    /* ─── edit ───────────────────────────────────────────────── */
    const openEdit = (record) => {
        setCurrentItem(record);
        editForm.setFieldsValue({
            spCode: record.spCode,
            spName: record.spName,
            spcId: record.spcId,
            uomId: record.uomId || undefined,
            spType: Number(record.spType),
            spBarcode: record.spBarcode,
            spUnitKg: record.spUnitKg,
            spOpeningquantity: record.spOpeningquantity,
            spCostprice: record.spCostprice,
            spSellingprice: record.spSellingprice,
            spSpecialprice: record.spSpecialprice,
        });
        setIsModalVisible(true);
    };

    const handleEdit = async (values) => {
        try {
            setLoading(true);
            const res = await api.post('/salesproducts/edit.php', {
                sp_id: currentItem.spId,
                sp_code: values.spCode,
                sp_name: values.spName,
                spc_id: values.spcId,
                uom_id: values.uomId,
                sp_type: values.spType,
                sp_barcode: values.spBarcode || '',
                // Opening quantity is intentionally omitted - it is captured at
                // creation and the API refuses to change it.
                sp_unit_kg: values.spUnitKg,
                sp_costprice: values.spCostprice,
                sp_sellingprice: values.spSellingprice,
                sp_specialprice: values.spSpecialprice,
            });
            if (res.data.status === 'success') {
                message.success(res.data.message || "Sales product updated successfully.");
                setIsModalVisible(false);
                fetchProducts(pagination.current, pagination.pageSize, applied);
                editForm.resetFields();
            } else throw new Error(res.data.message);
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to update sales product.");
        } finally { setLoading(false); }
    };

    /* ─── barcode scanning ───────────────────────────────────── */
    const openScanner = (target) => {
        setScanTarget(target);
        setScannerOpen(true);
    };

    const handleBarcodeDetected = (code) => {
        const value = String(code || '').trim();
        if (!value) return;
        const form = scanTarget === 'edit' ? editForm : addForm;
        form.setFieldValue('spBarcode', value);
        setScannerOpen(false);
        message.success(`Barcode captured: ${value}`);
    };

    /* ─── toggle / delete ────────────────────────────────────── */
    const handleToggle = async (record, checked) => {
        try {
            const res = await api.post('/salesproducts/toggle.php', {
                sp_id: record.spId,
                is_active: checked ? 1 : 0,
            });
            if (res.data.status === 'success') {
                message.success(res.data.message);
                fetchProducts(pagination.current, pagination.pageSize, applied);
            } else throw new Error(res.data.message);
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to update status.");
        }
    };

    const handleDelete = async (record) => {
        try {
            setLoading(true);
            const res = await api.post('/salesproducts/delete.php', { sp_id: record.spId });
            if (res.data.status === 'success') {
                message.success(res.data.message || "Sales product deleted successfully.");
                fetchProducts(pagination.current, pagination.pageSize, applied);
            } else throw new Error(res.data.message);
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to delete sales product.");
        } finally { setLoading(false); }
    };

    /* ─── exports ────────────────────────────────────────────── */
    const fetchRowsForExport = async () => {
        const res = await api.get('/salesproducts/list.php', {
            params: {
                page: 1,
                limit: EXPORT_LIMIT,
                search: applied.search || '',
                spc_id: applied.spc_id || 0,
                is_active: applied.is_active ?? -1,
            },
        });
        const rows = (res.data.dataset || []).map(mapRow);
        if (rows.length >= EXPORT_LIMIT) {
            message.warning(`Only the first ${EXPORT_LIMIT} matching products were exported. Narrow the filters to export the rest.`);
        }
        return rows;
    };

    const buildExportRows = (rows) => rows.map((p) => [
        p.spCode || '-',
        p.spName || '-',
        p.categoryName || '-',
        p.uomName ? (p.uomSymbol ? `${p.uomName} (${p.uomSymbol})` : p.uomName) : '-',
        productTypeLabel(p.spType),
        Number(p.spUnitKg || 0).toFixed(2),
        Number(p.spOpeningquantity || 0).toFixed(2),
        Number(p.spCostprice || 0).toFixed(2),
        Number(p.spSellingprice || 0).toFixed(2),
        Number(p.spSpecialprice || 0).toFixed(2),
        p.spBarcode || '-',
        p.isActive == 1 ? 'Active' : 'Inactive',
    ]);

    const EXPORT_HEADERS = [
        'Code', 'Product Name', 'Category', 'UOM', 'Type',
        'Unit Weight (Kg)', 'Opening Qty', 'Cost Price', 'Selling Price', 'Special Price', 'Barcode', 'Status',
    ];

    const exportToPDF = async () => {
        try {
            setExporting(true);
            const rows = await fetchRowsForExport();
            if (rows.length === 0) {
                message.warning("No data to export.");
                return;
            }

            const doc = new jsPDF({ orientation: 'landscape' });
            const pageWidth = doc.internal.pageSize.getWidth();

            doc.setFontSize(16);
            doc.setFont("helvetica", "bold");
            doc.setTextColor(45, 90, 61);
            doc.text("Sales Product List", 14, 20);

            doc.setFontSize(10);
            doc.setFont("helvetica", "normal");
            doc.setTextColor(100);
            doc.text(`Generated on ${dayjs().format("YYYY-MM-DD HH:mm")}`, 14, 28);
            doc.text(`Records: ${rows.length}`, pageWidth - 14, 28, { align: "right" });

            autoTable(doc, {
                head: [EXPORT_HEADERS],
                body: buildExportRows(rows),
                startY: 35,
                theme: "striped",
                margin: { left: 10, right: 10 },
                headStyles: { fillColor: [45, 90, 61], fontSize: 8, halign: "center" },
                styles: { fontSize: 7.5, cellPadding: 1.5, overflow: 'linebreak' },
                columnStyles: {
                    0: { cellWidth: 22 },
                    1: { cellWidth: 34 },
                    2: { cellWidth: 24 },
                    3: { cellWidth: 22 },
                    4: { cellWidth: 21, halign: 'center' },
                    5: { cellWidth: 21, halign: 'right' },
                    6: { cellWidth: 20, halign: 'right' },
                    7: { cellWidth: 21, halign: 'right' },
                    8: { cellWidth: 22, halign: 'right' },
                    9: { cellWidth: 22, halign: 'right' },
                    10: { cellWidth: 26 },
                    11: { cellWidth: 16, halign: 'center' },
                },
            });

            doc.save(`sales-products-${dayjs().format("YYYYMMDD-HHmm")}.pdf`);
            message.success("PDF exported successfully.");
        } catch {
            message.error("Failed to export PDF.");
        } finally {
            setExporting(false);
        }
    };

    const exportToExcel = async () => {
        try {
            setExporting(true);
            const rows = await fetchRowsForExport();
            if (rows.length === 0) {
                message.warning("No data to export.");
                return;
            }

            const aoa = [EXPORT_HEADERS, ...buildExportRows(rows)];
            const ws = XLSX.utils.aoa_to_sheet(aoa);
            ws["!cols"] = EXPORT_HEADERS.map(() => ({ wch: 20 }));
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Sales Products");
            XLSX.writeFile(wb, `sales-products-${dayjs().format("YYYYMMDD-HHmm")}.xlsx`);
            message.success("Excel exported successfully.");
        } catch {
            message.error("Failed to export Excel.");
        } finally {
            setExporting(false);
        }
    };

    /* ─── columns ────────────────────────────────────────────── */
    const columns = [
        {
            title: 'Product Code', dataIndex: 'spCode', key: 'spCode', width: 140,
            ...getColumnSearchProps('spCode'),
            render: (val) => <Text code>{val}</Text>,
        },
        {
            title: 'Barcode', dataIndex: 'spBarcode', key: 'spBarcode', width: 170,
            ...getColumnSearchProps('spBarcode'),
            render: (val) => val || <Text type="secondary">—</Text>,
        },
        {
            title: 'Product Name', dataIndex: 'spName', key: 'spName',
            sorter: (a, b) => (a.spName || '').localeCompare(b.spName || ''),
            ...getColumnSearchProps('spName'),
        },
        {
            title: 'Category', dataIndex: 'categoryName', key: 'categoryName', width: 180,
            render: (val) => val || <Text type="secondary">—</Text>,
        },
        {
            title: 'Cost Price', dataIndex: 'spCostprice', key: 'spCostprice', width: 150, align: 'right',
            render: (val) => fmtMoney(val),
        },
        {
            title: 'Selling Price', dataIndex: 'spSellingprice', key: 'spSellingprice', width: 150, align: 'right',
            render: (val) => fmtMoney(val),
        },
    ];

    // "View Full Details" is always available; the manage actions stay behind
    // the sales_products_manage permission.
    columns.push({
        title: 'Action', key: 'action', align: 'center', fixed: 'right', width: canManage ? 190 : 110,
        render: (_, record) => (
            <Space>
                <Tooltip title="View Full Details">
                    <Button type="text" icon={<EyeOutlined />} onClick={() => openView(record)} />
                </Tooltip>
                {canManage && (
                    <>
                        <Tooltip title="Edit Product">
                            <Button type="text" icon={<EditOutlined />} onClick={() => openEdit(record)} />
                        </Tooltip>
                        <Tooltip title={record.isActive == 1 ? "Deactivate" : "Activate"}>
                            <Switch
                                size="small"
                                checked={record.isActive == 1}
                                onChange={(checked) => handleToggle(record, checked)}
                            />
                        </Tooltip>
                        <Popconfirm
                            title="Delete Product"
                            description="This product will be removed from the list."
                            okText="Yes, Delete"
                            cancelText="No"
                            okButtonProps={{ danger: true }}
                            onConfirm={() => handleDelete(record)}
                        >
                            <Tooltip title="Delete Product">
                                <Button type="text" danger icon={<DeleteOutlined />} />
                            </Tooltip>
                        </Popconfirm>
                    </>
                )}
            </Space>
        ),
    });

    /* ─── shared form fields ─────────────────────────────────── */
    const renderProductFormFields = (target) => (
        <>
            <Row gutter={[16, 0]}>
                <Col xs={24} md={8}>
                    <Form.Item
                        label="Product Code" name="spCode"
                        rules={[
                            { required: true, message: 'Product code is required' },
                            { max: 50, message: 'Max 50 characters' },
                            { pattern: /^[A-Za-z0-9\-_]+$/, message: 'Only letters, numbers, hyphens and underscores' },
                        ]}
                    >
                        <Input placeholder="e.g. PRD-001" />
                    </Form.Item>
                </Col>
                <Col xs={24} md={16}>
                    <Form.Item
                        label="Product Name" name="spName"
                        rules={[
                            { required: true, message: 'Product name is required' },
                            { max: 150, message: 'Max 150 characters' },
                        ]}
                    >
                        <Input placeholder="e.g. Organic Fertilizer 50kg" />
                    </Form.Item>
                </Col>
            </Row>

            <Row gutter={[16, 0]}>
                <Col xs={24} md={8}>
                    <Form.Item label="Category" name="spcId" rules={[{ required: true, message: 'Category is required' }]}>
                        <Select placeholder="Select category" allowClear showSearch optionFilterProp="children">
                            {categories.map((cat) => (
                                <Option key={cat.value} value={Number(cat.value)}>{cat.label}</Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                    <Form.Item label="Unit of Measurement" name="uomId" rules={[{ required: true, message: 'UOM is required' }]}>
                        <Select placeholder="Select UOM" allowClear showSearch optionFilterProp="children">
                            {uoms.map((uom) => (
                                <Option key={uom.value} value={Number(uom.value)}>
                                    {uom.label}{uom.uom_symbol ? ` (${uom.uom_symbol})` : ''}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                    <Form.Item label="Product Type" name="spType" rules={[{ required: true, message: 'Product type is required' }]}>
                        <Select placeholder="Select type">
                            {PRODUCT_TYPE_OPTIONS.map((opt) => (
                                <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>
            </Row>

            <Row gutter={[16, 0]}>
                <Col xs={24} md={8}>
                    <Form.Item
                        label="Unit Weight (Kg)" name="spUnitKg"
                        rules={[
                            { required: true, message: 'Unit weight is required' },
                            { pattern: /^\d+(\.\d+)?$/, message: 'Enter a valid weight' },
                        ]}
                    >
                        <Input type="number" min="0" step="0.01" placeholder="0.00" />
                    </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                    <Form.Item
                        label="Opening Quantity" name="spOpeningquantity"
                        rules={target === 'add' ? [
                            { required: true, message: 'Opening quantity is required' },
                            { pattern: /^\d+(\.\d+)?$/, message: 'Enter a valid quantity' },
                        ] : []}
                        extra={target === 'edit'
                            ? 'Opening quantity is captured at creation and cannot be changed.'
                            : 'Stock on hand when the product was created (use 0 if none).'}
                    >
                        <Input
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder="0.00"
                            disabled={target === 'edit'}
                        />
                    </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                    <Form.Item
                        label="Cost Price (LKR)" name="spCostprice"
                        rules={[
                            { required: true, message: 'Cost price is required' },
                            { pattern: /^\d+(\.\d+)?$/, message: 'Enter a valid amount' },
                        ]}
                    >
                        <Input type="number" min="0" step="0.01" placeholder="0.00" />
                    </Form.Item>
                </Col>
            </Row>

            <Row gutter={[16, 0]}>
                <Col xs={24} md={8}>
                    <Form.Item
                        label="Selling Price (LKR)" name="spSellingprice"
                        rules={[
                            { required: true, message: 'Selling price is required' },
                            { pattern: /^\d+(\.\d+)?$/, message: 'Enter a valid amount' },
                        ]}
                    >
                        <Input type="number" min="0" step="0.01" placeholder="0.00" />
                    </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                    <Form.Item
                        label="Special Price (LKR)" name="spSpecialprice"
                        rules={[
                            { required: true, message: 'Special price is required' },
                            { pattern: /^\d+(\.\d+)?$/, message: 'Enter a valid amount' },
                        ]}
                    >
                        <Input type="number" min="0" step="0.01" placeholder="0.00" />
                    </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                    <Form.Item
                        label="Barcode (optional)" name="spBarcode"
                        rules={[{ max: 100, message: 'Max 100 characters' }]}
                    >
                        <Input
                            placeholder="Scan or type barcode"
                            onPressEnter={(e) => e.preventDefault()}
                            addonAfter={
                                <Tooltip title="Scan barcode (camera or hardware scanner)">
                                    <Button
                                        type="text"
                                        size="small"
                                        icon={<ScanOutlined />}
                                        onClick={() => openScanner(target)}
                                        aria-label="Scan barcode"
                                        style={{ margin: -4 }}
                                    />
                                </Tooltip>
                            }
                        />
                    </Form.Item>
                </Col>
            </Row>
        </>
    );

    /* ─── render ─────────────────────────────────────────────── */
    return (
        <Layout className="fluid-page">
            <Headerdiv />

            <div className="fluid-page-header">
                <div className="floating-shape shape-1"></div>
                <div className="floating-shape shape-2"></div>
                <Row align="bottom" justify="space-between" gutter={[16, 12]}>
                    <Col>
                        <Breadcrumb
                            className="fluid-breadcrumb"
                            items={[
                                { href: '/dash', title: <HomeOutlined /> },
                                { title: 'Sales' },
                                { title: 'Sales Products' },
                            ]}
                        />
                        <h1 className="fluid-page-title">
                            <ShoppingCartOutlined /> Sales Products
                        </h1>
                        <p className="fluid-page-subtitle">Manage products available for sale</p>
                    </Col>
                    {canManage && (
                        <Col xs={24} md={12} style={{ textAlign: 'right' }}>
                            <div className="hero-actions" style={{ marginTop: 0, justifyContent: 'flex-end' }}>
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={() => setIsAddModalVisible(true)}
                                    className="hero-action-btn"
                                >
                                    Add New Product
                                </Button>
                            </div>
                        </Col>
                    )}
                </Row>
            </div>

            <Content className="fluid-content">
                <div className="fluid-card">
                    <div className="fluid-filter-compact" style={{ marginBottom: 12 }}>
                        <Row gutter={16} align="bottom" className="fluid-filter-row">
                            <Col xs={24} sm={12} lg={5}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Search:</div>
                                <Input
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    onPressEnter={handleFilter}
                                    placeholder="Code, name, barcode"
                                    prefix={<SearchOutlined />}
                                    allowClear
                                />
                            </Col>
                            <Col xs={24} sm={12} lg={4}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Category:</div>
                                <Select
                                    style={{ width: "100%" }}
                                    value={filterCat}
                                    onChange={setFilterCat}
                                    placeholder="All Categories"
                                    allowClear
                                    showSearch
                                    optionFilterProp="children"
                                >
                                    {categories.map((cat) => (
                                        <Option key={cat.value} value={Number(cat.value)}>{cat.label}</Option>
                                    ))}
                                </Select>
                            </Col>
                            <Col xs={24} sm={12} lg={3}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Status:</div>
                                <Select style={{ width: "100%" }} value={filterStatus} onChange={setFilterStatus}>
                                    <Option value="all">All Status</Option>
                                    <Option value="active">Active</Option>
                                    <Option value="inactive">Inactive</Option>
                                </Select>
                            </Col>
                            <Col xs={24} sm={12} lg={4}>
                                <div className="fluid-filter-compact-actions">
                                    <Button type="primary" icon={<FilterOutlined />} onClick={handleFilter} className="fluid-btn-primary">
                                        Apply
                                    </Button>
                                    <Button icon={<ClearOutlined />} onClick={handleClear} className="fluid-btn-secondary">
                                        Clear
                                    </Button>
                                </div>
                            </Col>
                            <Col xs={24} lg={8} style={{ textAlign: "right" }}>
                                <Space wrap>
                                    <Tooltip title="Export to PDF">
                                        <Button
                                            icon={<FilePdfOutlined style={{ color: "#ff4d4f" }} />}
                                            onClick={exportToPDF}
                                            loading={exporting}
                                        >
                                            PDF
                                        </Button>
                                    </Tooltip>
                                    <Tooltip title="Export to Excel">
                                        <Button
                                            icon={<FileExcelOutlined style={{ color: "#52c41a" }} />}
                                            onClick={exportToExcel}
                                            loading={exporting}
                                        >
                                            Excel
                                        </Button>
                                    </Tooltip>
                                </Space>
                            </Col>
                        </Row>
                    </div>

                    <div className="fluid-table">
                        <Table
                            size="small"
                            columns={columns}
                            dataSource={products}
                            rowKey="spId"
                            pagination={{
                                current: pagination.current,
                                pageSize: pagination.pageSize,
                                total: pagination.total,
                                showSizeChanger: true,
                                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                            }}
                            onChange={handleTableChange}
                            loading={loading}
                            scroll={{ x: 1200 }}
                        />
                    </div>
                </div>
            </Content>

            <Footerdiv />

            <BarcodeScannerModal
                open={scannerOpen}
                onClose={() => setScannerOpen(false)}
                onDetected={handleBarcodeDetected}
            />

            {/* Add Modal */}
            <Modal
                title="Add New Sales Product"
                open={isAddModalVisible}
                onCancel={() => { setIsAddModalVisible(false); addForm.resetFields(); }}
                footer={null}
                width={820}
                destroyOnClose
            >
                <Form form={addForm} layout="vertical" onFinish={handleAdd}>
                    {renderProductFormFields('add')}
                    <Row justify="end">
                        <Form.Item style={{ marginBottom: 0 }}>
                            <Space>
                                <Button danger onClick={() => { setIsAddModalVisible(false); addForm.resetFields(); }}>Cancel</Button>
                                <Button type="primary" htmlType="submit" icon={<PlusOutlined />} loading={loading}>Add Product</Button>
                            </Space>
                        </Form.Item>
                    </Row>
                </Form>
            </Modal>

            {/* Edit Modal */}
            <Modal
                title="Edit Sales Product"
                open={isModalVisible}
                onCancel={() => { setIsModalVisible(false); editForm.resetFields(); }}
                footer={null}
                width={820}
                destroyOnClose
            >
                <Form form={editForm} onFinish={handleEdit} layout="vertical">
                    {renderProductFormFields('edit')}
                    <Row justify="end">
                        <Form.Item style={{ marginBottom: 0 }}>
                            <Space>
                                <Button danger onClick={() => { setIsModalVisible(false); editForm.resetFields(); }}>Cancel</Button>
                                <Button type="primary" htmlType="submit" loading={loading}>Save Changes</Button>
                            </Space>
                        </Form.Item>
                    </Row>
                </Form>
            </Modal>

            {/* View Full Details Modal */}
            <Modal
                title="Sales Product Full Details"
                open={isViewVisible}
                onCancel={() => setIsViewVisible(false)}
                footer={<Button type="primary" onClick={() => setIsViewVisible(false)}>Close</Button>}
                width={760}
                maskClosable={false}
                destroyOnClose
            >
                {viewItem && (
                    <Descriptions bordered size="small" column={2}>
                        <Descriptions.Item label="Product Code">
                            <Text code strong>{viewItem.spCode}</Text>
                        </Descriptions.Item>
                        <Descriptions.Item label="Barcode">{viewItem.spBarcode || "—"}</Descriptions.Item>
                        <Descriptions.Item label="Product Name" span={2}>{viewItem.spName || "—"}</Descriptions.Item>
                        <Descriptions.Item label="Category">{viewItem.categoryName || "—"}</Descriptions.Item>
                        <Descriptions.Item label="Unit of Measurement">
                            {viewItem.uomName
                                ? (viewItem.uomSymbol ? `${viewItem.uomName} (${viewItem.uomSymbol})` : viewItem.uomName)
                                : "—"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Product Type">
                            <Tag color={Number(viewItem.spType) === 1 ? 'blue' : 'purple'}>
                                {productTypeLabel(viewItem.spType)}
                            </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Status">
                            {viewItem.isActive == 1
                                ? <Tag color="green">Active</Tag>
                                : <Tag color="red">Inactive</Tag>}
                        </Descriptions.Item>
                        <Descriptions.Item label="Unit Weight (Kg)">{Number(viewItem.spUnitKg || 0).toFixed(2)}</Descriptions.Item>
                        <Descriptions.Item label="Opening Quantity">{Number(viewItem.spOpeningquantity || 0).toFixed(2)}</Descriptions.Item>
                        <Descriptions.Item label="Cost Price (LKR)">{fmtMoney(viewItem.spCostprice)}</Descriptions.Item>
                        <Descriptions.Item label="Selling Price (LKR)">{fmtMoney(viewItem.spSellingprice)}</Descriptions.Item>
                        <Descriptions.Item label="Special Price (LKR)" span={2}>{fmtMoney(viewItem.spSpecialprice)}</Descriptions.Item>
                        <Descriptions.Item label="Created At">
                            {viewItem.createdAt ? dayjs(viewItem.createdAt).format("YYYY-MM-DD HH:mm") : "—"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Last Updated">
                            {viewItem.updatedAt ? dayjs(viewItem.updatedAt).format("YYYY-MM-DD HH:mm") : "—"}
                        </Descriptions.Item>
                    </Descriptions>
                )}
            </Modal>
        </Layout>
    );
};

export default ManageSalesProducts;
